import {Component, Input, OnInit} from '@angular/core';
import { ICurrency } from 'libraries/service/resources/model/search/ICurrency';
import { IDetailsOffer, OfferStatus } from 'libraries/hotel/models/details';
import { IConfigSharedHotel } from 'libraries/hotel/models/config-shared-hotel';
import { HotelCurrencyService } from 'libraries/service/hotel-currency/hotel-currency.service';
import { QuotationService } from 'libraries/hotel/services/quotation/quotation.service';
import { HelperService } from 'libraries/hotel/services/helper.service';
import { ICurrencies } from 'libraries/service/resources/hotel/ISearchResponse';
import { faCheck, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { EStayPermissions } from "libraries/enums/e-stay-permissions";
import {PermissionResourceService} from "../../../service/resources/permission/permissionResource.service";
import {Yii} from "../../../interfaces/Yii";

class WordingClass {
    public readonly cost: string = Yii.t('wui-iqbh-js', 'Prix d\'achat');
    public readonly retail: string = Yii.t('wui-iqbh-js', 'Prix de vente');
}

@Component({
    selector: 'offer',
    template: `
        <div class="offer">
            <div class="host-icon">
                <div class="selected-host">
                    <fa-icon *ngIf="isSelectedOffer" [icon]="iconCheck"></fa-icon>
                </div>

                <h2 class="host"
                    *permissionIf="permissions.SHARED_HOST_SHOW"
                    [class.isNotSelectedOffer]="!isSelectedOffer">
                    {{ offer.host | hostAlias | async }}
                </h2>
            </div>

            <div class="content">
                <div class="properties">

                    <request-status *ngIf="offer.status === OfferStatus.Request"></request-status>

                    <div class="mealPlan">
                        <meal-plan [name]="offer.mealplan?.name?.text"
                                   [code]="offer.mealplan?.xCode">
                        </meal-plan>
                    </div>

                    <cancellation-conditions [dateLastCancellationWithoutPenalty]="offer.dateLastCancellationWithoutPenalty"
                                             [currency]="currency"
                                             [price]="offer.total.cost"
                                             [configSharedHotel]="configSharedHotel"></cancellation-conditions>
                </div>
                <div class="price">
                    <ng-container *ngIf="offer?.total">
                        <ng-container *ngIf="showCostPrice">
                            <span class="priceWording" *ngIf="showRetailPrice && offer.total.retail">{{wording.cost}}</span>
                            <price-details *ngIf="offer?.total.cost"
                                           [cost]="offer.total.cost"
                                           [currencies]="currencies"></price-details>
                        </ng-container>

                        <ng-container *ngIf="showRetailPrice && offer.total.retail">
                            <span class="priceWording" *ngIf="offer.total.cost && showCostPrice">{{wording.retail}}</span>
                            <price-details [cost]="offer.total.retail"
                                           [currencies]="currencies">
                            </price-details>
                        </ng-container>
                    </ng-container>
                </div>
            </div>
        </div>
    `,
    styles: [`
        .priceWording {
            font-size: 0.7em;
        }
        
        .offer {
            display: flex;
            justify-content: space-between;
        }

        .host-icon {
            display: flex;
            align-items: baseline;
            flex: 5;
            margin-right: 40px;
        }

        .selected-host {
            width: 30px;
            font-size: 1.25rem;
            color: #3c763d;
        }

        .host {
            font-size: 1.25rem;
        }

        .content {
            flex: 5;
            display: flex;
        }

        .properties {
            flex: 5;
        }

        .price {
            font-size: 1.5rem;
            flex: 2;
            font-weight: bold;
            text-align: right;
        }

        .price ::ng-deep .symbol {
            font-size: .90rem;
        }

        .mealPlan,
        cancellation-conditions {
            display: block;
            margin-top: 5px;
            margin-bottom: 5px;
        }
    `]
})
export class OfferComponent implements OnInit{
    @Input()
    public offer: IDetailsOffer;

    @Input()
    public currency: ICurrency;

    @Input()
    public currencies: ICurrencies;

    @Input()
    public configSharedHotel: IConfigSharedHotel;

    @Input()
    public isSelectedOffer: boolean;

    public iconCheck: IconDefinition = faCheck;

    public OfferStatus: typeof OfferStatus = OfferStatus;
    public showRetailPrice: boolean = false;
    public showCostPrice: boolean = false;
    public readonly wording: WordingClass = new WordingClass();

    constructor(
        private readonly hotelCurrencyService: HotelCurrencyService,
        private readonly quotationService: QuotationService,
        private readonly helperService: HelperService,
        private readonly permissionResource: PermissionResourceService
    ) {

    }

    public ngOnInit() {
        this.permissionResource.getPermissionsStartingWith(['shared', 'stay', 'price']).subscribe((permissions: Set<string>) => {
            this.showRetailPrice = permissions.has('shared.stay.price.retail.show');
            this.showCostPrice = permissions.has('shared.stay.price.cost.show');
        });
    }

    public getPrice(price: { [key: string]: number }): number {
        const currency: ICurrency = {...this.currency};
        if (this.configSharedHotel) {
            currency.code = this.hotelCurrencyService.getCurrencyString(price, this.configSharedHotel);
        }
        return this.quotationService.getPrice(price, currency);
    }

    public getDateFormat(): string {
        return this.helperService.getDateFormat().momentShort;
    }

    public permissions: typeof EStayPermissions = EStayPermissions;

}
